import { useEffect, useState } from 'react';
import type { ExpenseTransition } from '../types/expense';
import { getExpenseTransitions } from '../lib/api/endpoints';
import { expenseTransitionsFromDto } from '../lib/api/mappers';

/**
 * FE-12 — la novedad de UN gasto: quién lo registró, quién lo corrigió, y el
 * "de qué a qué" de cada campo.
 *
 * Se pide bajo demanda (al abrir el detalle), no junto al listado: serían N+1
 * peticiones si se pidiera por cada fila, y la inmensa mayoría de los gastos
 * nunca se corrigen — su novedad es una sola línea.
 *
 * `expenseId` en `null` limpia el estado en vez de dejar colgada la novedad
 * del gasto anterior: al cerrar el detalle y abrir otro, lo que se pinta
 * mientras carga tiene que ser vacío, no la corrección de otro gasto.
 */
export function useExpenseTransitions(expenseId: string | null) {
  const [transitions, setTransitions] = useState<ExpenseTransition[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!expenseId) {
      setTransitions([]);
      setError(null);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      setTransitions([]);
      try {
        const res = await getExpenseTransitions(expenseId);
        if (cancelled) return;
        setTransitions(expenseTransitionsFromDto(res.items));
        setError(null);
      } catch (err) {
        if (!cancelled) {
          setTransitions([]);
          setError(err instanceof Error ? err : new Error('Error desconocido cargando la novedad del gasto'));
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [expenseId]);

  return { transitions, isLoading, error };
}
